/**
 * Wave-Viz 3：管理视角「销售排名 + 掉队红旗 + 健康度恶化」卡（V-FE-dashboard）
 *
 * 调 GET /dashboard/team-rank，端点契约：
 *   { ranking:[{userId,name,dealCount,dealAmount}],
 *     laggards:[{userId,name,lastFollowupAt,days}],
 *     riskCustomers:[{id,name,healthScore}] }
 * 后端 @RequirePermissions('report:view_team') + service 内强制 role∈manager/director/admin + data_scope
 *   （manager 限本团队、director/admin 全公司；sales 即便持 view_team 也被 ForbiddenException 挡）。
 *
 * 三块：①本周签单排名（金额/单数，复用 month-performance 严格口径）；②掉队红旗（X 天零跟进，
 *   口径对齐 dashboard.alerts staleFollowUp）；③健康度恶化 TOP（<40 红，复用 ui.healthLevel 阈值）。
 *
 * 仅 role∈manager/director/admin 显示（前端门控，宿主在 isPrivileged 分支挂载并叠加 role 判定）。
 * 复用 HomeCard/Avatar/Pill/healthLevel 原语；金额用 formatMoneyShort（万口径，与看板一致）。
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { $ } from '../../lib/api';
import { HomeCard, Avatar, Pill, healthLevel } from '../ui';
import { formatMoneyShort } from '../../lib/format';

type RankRow = { userId: number; name: string; dealCount: number; dealAmount: number };
type Laggard = { userId: number; name: string; lastFollowupAt: string | null; days: number | null };
type RiskCustomer = { id: number; name: string; healthScore: number };

type TeamRankData = {
  ranking: RankRow[];
  laggards: Laggard[];
  riskCustomers: RiskCustomer[];
};

const HEALTH_TONE: Record<string, 'success' | 'warning' | 'danger'> = {
  green: 'success', yellow: 'warning', red: 'danger',
};

export function TeamRankCard() {
  const { data } = useQuery<TeamRankData>({
    queryKey: ['dashboard/team-rank'],
    queryFn: () => $.get('/dashboard/team-rank'),
    refetchInterval: 120_000,
  });

  const ranking = data?.ranking ?? [];
  const laggards = data?.laggards ?? [];
  const riskCustomers = data?.riskCustomers ?? [];

  return (
    <div className="home-grid">
      {/* ① 本周签单排名 */}
      <HomeCard
        title={<>本周签单排名 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>按审批通过口径</span></>}
        action={<Link to="/admin/sales-targets">详情</Link>}
      >
        <div style={{ padding: '4px 0' }}>
          {ranking.length === 0 && (
            <div style={{ padding: '20px 0', color: 'var(--text-3)', fontSize: 13, textAlign: 'center' }}>
              本周暂无签单
            </div>
          )}
          {ranking.slice(0, 8).map((r, i) => (
            <div key={r.userId} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 16px', borderBottom: '1px solid var(--border)',
            }}>
              <span style={{
                width: 24, height: 24, borderRadius: 12, lineHeight: '24px', textAlign: 'center',
                background: i < 3 ? '#fa8c16' : 'var(--bg-3)',
                color: i < 3 ? '#fff' : 'var(--text-2)',
                fontSize: 12, fontWeight: 600,
              }}>{i + 1}</span>
              <Avatar userId={r.userId} name={r.name} size="sm" />
              <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{r.name}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginRight: 4 }}>{r.dealCount} 单</span>
              <span style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}>
                {formatMoneyShort(r.dealAmount)}
              </span>
            </div>
          ))}
        </div>
      </HomeCard>

      {/* ② 掉队红旗 + ③ 健康度恶化 TOP（同一卡分两段，节省版面）*/}
      <HomeCard title={<>掉队红旗 & 健康预警 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>需介入</span></>}>
        {/* ② 掉队红旗 */}
        <div style={{
          padding: '8px 14px', fontSize: 12, fontWeight: 500,
          color: 'var(--danger)', background: 'var(--bg-2)', borderLeft: '3px solid var(--danger)',
        }}>
          🔴 长时间零跟进 <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>({laggards.length})</span>
        </div>
        {laggards.length === 0 && (
          <div style={{ padding: '12px 16px', color: 'var(--text-3)', fontSize: 13 }}>暂无掉队成员</div>
        )}
        {laggards.slice(0, 5).map((l) => (
          <Link key={l.userId} to={`/customers?owner=${l.userId}&stale=7`} style={{ textDecoration: 'none' }}>
            <div className="todo-item" style={{ paddingTop: 10, paddingBottom: 10 }}>
              <Avatar userId={l.userId} name={l.name} size="sm" />
              <div className="todo-content">
                <div className="todo-title">{l.name}</div>
                <div className="todo-meta" style={{ color: 'var(--danger)' }}>{l.days == null ? '从未跟进' : `${l.days} 天零跟进`}</div>
              </div>
              <span className="todo-action">查看</span>
            </div>
          </Link>
        ))}

        {/* ③ 健康度恶化 TOP */}
        <div style={{
          padding: '8px 14px', fontSize: 12, fontWeight: 500, marginTop: 6,
          color: '#fa8c16', background: 'var(--bg-2)', borderLeft: '3px solid #fa8c16',
        }}>
          🟠 健康度恶化客户 <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>({riskCustomers.length})</span>
        </div>
        {riskCustomers.length === 0 && (
          <div style={{ padding: '12px 16px', color: 'var(--text-3)', fontSize: 13 }}>暂无低健康度客户</div>
        )}
        {riskCustomers.slice(0, 5).map((c) => (
          <Link key={c.id} to={`/customers/${c.id}`} style={{ textDecoration: 'none' }}>
            <div className="todo-item" style={{ paddingTop: 10, paddingBottom: 10 }}>
              <div className="todo-content">
                <div className="todo-title">{c.name}</div>
              </div>
              <Pill tone={HEALTH_TONE[healthLevel(c.healthScore)]}>健康度 {c.healthScore}</Pill>
            </div>
          </Link>
        ))}
      </HomeCard>
    </div>
  );
}
